/**
 * Compliance Status Resolver — IN-1.b
 * ───────────────────────────────────
 * Pure resolver: joins the entity-type requirements matrix (IN-1.a)
 * with the document ledger (this phase) to derive per-requirement and
 * overall compliance status.
 *
 * IN-1.b is **observation-only**. This resolver MUST NOT be wired into:
 *   • dispatch blocking
 *   • offer eligibility
 *   • vetting promotion hard gate
 *   • payout
 *   • suspension
 *   • onboarding UI
 *
 * Anchor: RECONCILIATION.md §13 (IN-1.b)
 */

import type { Contractor } from "@/data/types";
import {
  getRequiredCompliance,
  type ComplianceRequirements,
} from "./complianceRequirements";
import {
  isActiveDocument,
  type ComplianceDocument,
  type ComplianceDocumentType,
} from "./complianceDocuments";

export type ComplianceRequirementKey =
  | "license"
  | "insurance"
  | "w9"
  | "background_check"
  | "agreement"
  | "banking_verification"
  | "certification";

export type RequirementStatus =
  | "ok"
  | "missing"
  | "expired"
  | "expiring_soon"
  | "pending_verification"
  | "rejected"
  | "not_required";

export type OverallComplianceStatus =
  | "ok"
  | "missing"
  | "expired"
  | "expiring_soon"
  | "pending_verification"
  | "not_required";

export interface RequirementResolution {
  requirement: ComplianceRequirementKey;
  required: boolean;
  inheritedFromParent?: boolean;
  status: RequirementStatus;
  documentId?: string;
  expiresAt?: string;
}

export interface ComplianceStatus {
  overall: OverallComplianceStatus;
  requirements: RequirementResolution[];
}

// 30-day "expiring soon" window (locked).
export const EXPIRING_SOON_WINDOW_DAYS = 30;

// Requirement → document type mapping (single source of truth for join).
const REQUIREMENT_DOC_TYPE: Record<ComplianceRequirementKey, ComplianceDocumentType> = {
  license: "license",
  insurance: "insurance",
  w9: "w9",
  background_check: "background_check",
  agreement: "agreement",
  banking_verification: "banking_verification",
  certification: "certification",
};

function daysBetween(a: Date, b: Date): number {
  const ms = a.getTime() - b.getTime();
  return ms / (1000 * 60 * 60 * 24);
}

function classifyDocument(
  doc: ComplianceDocument | undefined,
  now: Date,
): { status: RequirementStatus; documentId?: string; expiresAt?: string } {
  if (!doc) return { status: "missing" };
  if (doc.rejectedAt) return { status: "rejected", documentId: doc.id };
  if (!doc.verifiedAt) {
    return {
      status: "pending_verification",
      documentId: doc.id,
      expiresAt: doc.expiresAt,
    };
  }
  if (doc.expiresAt) {
    const exp = new Date(doc.expiresAt);
    if (!Number.isNaN(exp.getTime())) {
      const days = daysBetween(exp, now);
      if (days < 0) {
        return { status: "expired", documentId: doc.id, expiresAt: doc.expiresAt };
      }
      if (days <= EXPIRING_SOON_WINDOW_DAYS) {
        return {
          status: "expiring_soon",
          documentId: doc.id,
          expiresAt: doc.expiresAt,
        };
      }
    }
  }
  return { status: "ok", documentId: doc.id, expiresAt: doc.expiresAt };
}

/**
 * Return the best (active, verified, latest-expiring) document of a
 * given type for a contractor, ignoring superseded and rejected rows.
 * Falls back to: latest active pending, then latest rejected (for
 * surface-area reporting).
 */
function bestDocument(
  documents: ComplianceDocument[],
  contractorId: string,
  type: ComplianceDocumentType,
): ComplianceDocument | undefined {
  const all = documents.filter(
    (d) => d.contractorId === contractorId && d.type === type,
  );
  const active = all.filter(isActiveDocument);
  const verified = active.filter((d) => d.verifiedAt);
  if (verified.length) {
    return [...verified].sort((a, b) => {
      const ax = a.expiresAt ? Date.parse(a.expiresAt) : Number.POSITIVE_INFINITY;
      const bx = b.expiresAt ? Date.parse(b.expiresAt) : Number.POSITIVE_INFINITY;
      return bx - ax;
    })[0];
  }
  const pending = active.filter((d) => !d.verifiedAt);
  if (pending.length) {
    return [...pending].sort(
      (a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt),
    )[0];
  }
  // Surface most recent rejected for diagnostic context.
  if (all.length) {
    return [...all].sort(
      (a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt),
    )[0];
  }
  return undefined;
}

function resolveOne(
  requirement: ComplianceRequirementKey,
  required: boolean,
  contractor: Pick<Contractor, "id" | "parentEntityId">,
  parent: Pick<Contractor, "id"> | undefined,
  inheritedFromParent: boolean,
  documents: ComplianceDocument[],
  now: Date,
): RequirementResolution {
  if (!required) {
    return { requirement, required: false, status: "not_required" };
  }
  const docType = REQUIREMENT_DOC_TYPE[requirement];

  // Inherited requirements (e.g. branch/crew/helper inheriting parent
  // insurance) resolve from the parent's ledger when a parent is
  // supplied. If no parent context is provided, we cannot verify and
  // fall back to the child's own ledger so the resolver remains pure.
  const sourceContractorId =
    inheritedFromParent && parent?.id ? parent.id : contractor.id;

  const doc = bestDocument(documents, sourceContractorId, docType);
  const classified = classifyDocument(doc, now);
  return {
    requirement,
    required: true,
    inheritedFromParent: inheritedFromParent || undefined,
    ...classified,
  };
}

function rollUp(
  resolutions: RequirementResolution[],
): OverallComplianceStatus {
  const active = resolutions.filter((r) => r.required);
  if (!active.length) return "not_required";

  // Priority: missing/rejected > expired > pending_verification > expiring_soon > ok
  const hasMissing = active.some(
    (r) => r.status === "missing" || r.status === "rejected",
  );
  if (hasMissing) return "missing";
  if (active.some((r) => r.status === "expired")) return "expired";
  if (active.some((r) => r.status === "pending_verification"))
    return "pending_verification";
  if (active.some((r) => r.status === "expiring_soon")) return "expiring_soon";
  return "ok";
}

/**
 * Derive the compliance status for a contractor.
 *
 * @param contractor The contractor under evaluation.
 * @param documents  The full document ledger (any source). Will be
 *                   filtered by contractor id (and parent id when an
 *                   inherited requirement applies).
 * @param parent     Optional parent entity. Required for
 *                   `inheritedFromParent` requirements to resolve from
 *                   parent docs instead of the child's own ledger.
 */
export function getComplianceStatus(
  contractor: Pick<Contractor, "id" | "entityType" | "parentEntityId">,
  documents: ComplianceDocument[],
  parent?: Pick<Contractor, "id" | "entityType">,
  now: Date = new Date(),
): ComplianceStatus {
  const reqs: ComplianceRequirements = getRequiredCompliance(contractor, parent);

  const items: RequirementResolution[] = [
    resolveOne(
      "license",
      reqs.licenseRequired,
      contractor,
      parent,
      reqs.inheritsFromParent.license,
      documents,
      now,
    ),
    resolveOne(
      "insurance",
      reqs.insuranceRequired,
      contractor,
      parent,
      reqs.inheritsFromParent.insurance,
      documents,
      now,
    ),
    resolveOne(
      "w9",
      reqs.w9Required,
      contractor,
      parent,
      reqs.inheritsFromParent.w9,
      documents,
      now,
    ),
    resolveOne(
      "background_check",
      reqs.backgroundCheckRequired,
      contractor,
      parent,
      false,
      documents,
      now,
    ),
    // Agreement is always required (variant differs per entity type).
    resolveOne("agreement", true, contractor, parent, false, documents, now),
    resolveOne(
      "banking_verification",
      reqs.bankingRequired,
      contractor,
      parent,
      false,
      documents,
      now,
    ),
    // Certification is only required when the agreement variant signals it.
    resolveOne(
      "certification",
      reqs.agreementVariant === "certification_agreement",
      contractor,
      parent,
      false,
      documents,
      now,
    ),
  ];

  return { overall: rollUp(items), requirements: items };
}
